import React, { useMemo } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Divider,
    Grid,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { theme } from '../../../../../shared/theme';
import { getAttendeeLabel } from '../../../../../shared/utils';
import { ExperienceType } from '../../../ExperienceReviews/ReviewWizard';
import CameraEnhanceIcon from '@mui/icons-material/CameraEnhance';
import { People } from '@mui/icons-material';
import UserRole from '../../../../../shared/AuthRoles';
import { BookingAttendee } from '../../../../types/reservation';

type Attendee = {
    id?: string | number | null;
    email?: string | null;
    isActive?: boolean | null;
    firstName?: string | null;
    lastName?: string | null;
    name?: string | null;
};

type Props = {
    reservation: any;
    asset: any;
    expanded: boolean;
    onToggle: (event: React.SyntheticEvent, isExpanded: boolean) => void;
    onOpenNirovisionModal: () => void;
    onOpenRsvpModal: () => void;
    isBlockedByOwner: boolean;
};

export default function ReservationGuestsSection({
    reservation,
    asset,
    expanded,
    onToggle,
    onOpenNirovisionModal,
    onOpenRsvpModal,
    isBlockedByOwner,
}: Props) {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const loggedInUser = UserRole();

    const formatCount = (value: number | null | undefined) => value ?? 0;

    const isCommercial = asset?.type === ExperienceType.Commercial;
    const isOwnerBooker = reservation?.bookerType?.includes('owner');

    const showOvernightGuests =
        reservation?.bookingInfo?.type !== 'activity' &&
        reservation?.startDate !== reservation?.endDate;

    const staffVisible =
        reservation?.staffAttending &&
        asset?.visitType !== 'personal' &&
        asset?.type !== 'vehicle' &&
        (!isCommercial ||
            reservation?.bookingInfo?.meetingRooms != null ||
            reservation?.bookingInfo?.entireVenue ||
            reservation?.bookingInfo?.reqMeetingRoom ||
            isBlockedByOwner);

    const guestVisible =
        reservation?.externalAttendees &&
        reservation?.externalAttendees?.length >= 0 &&
        asset?.visitType !== 'personal' &&
        asset?.type !== 'vehicle';

    const adults = useMemo(() => {
        return ['activity', 'commercial'].includes(reservation?.module?.type)
            ? formatCount(reservation?.bookingInfo?.guests)
            : formatCount(reservation?.bookingInfo?.adults);
    }, [reservation]);

    const children = useMemo(() => {
        return reservation?.module?.type === 'activity'
            ? 0
            : formatCount(reservation?.bookingInfo?.children);
    }, [reservation]);

    const overnight = useMemo(() => {
        return showOvernightGuests
            ? formatCount(reservation?.bookingInfo?.overnightGuests)
            : 0;
    }, [reservation, showOvernightGuests]);

    const adultsLabel =
        reservation?.module?.type === 'activity' ? 'Guests' : 'Adults';

    const outerAccordionSx = {
        mt: 2,
        borderRadius: 2,
        boxShadow: 'none',
        '&::before': { display: 'none' },
        overflow: 'hidden',
    };

    const sectionTitleSx = {
        letterSpacing: 0.6,
        textTransform: 'uppercase' as const,
        color: theme.palette.text.secondary,
    };

    const cardSx = {
        backgroundColor: theme.palette.custom.cardActionBackground,
        borderRadius: 2,
        px: isMobile ? 1.5 : 2,
        py: 1.5,
    };

    const statCardSx = {
        ...cardSx,
        py: 1.25,
    };

    const statLabelSx = {
        fontWeight: 700,
        letterSpacing: 0.6,
        color: theme.palette.text.secondary,
        lineHeight: 1.2,
        fontSize: 14,
        textTransform: 'uppercase',
    };

    const statValueSx = {
        mt: 0.5,
        fontSize: 20,
        fontWeight: 700,
        color: theme.palette.text.primary,
        lineHeight: 1.1,
    };

    const attendeeBoxSx = {
        borderRadius: 2,
        backgroundColor: theme.palette.neutral.main,
        px: isMobile ? 1.5 : 2,
        py: 1.25,
    };

    const renderCommercialExtras = () => {
        if (reservation?.module?.type !== ExperienceType.Commercial)
            return null;

        const hasDesks = reservation?.bookingInfo?.desks != null;
        const hasPrivateOffices =
            reservation?.bookingInfo?.privateOffices != null;
        const hasMeetingRooms = reservation?.bookingInfo?.meetingRooms != null;
        const hasEntireVenue = reservation?.bookingInfo?.entireVenue;

        if (
            !hasDesks &&
            !hasPrivateOffices &&
            !hasMeetingRooms &&
            !hasEntireVenue
        )
            return null;

        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    mb: 2,
                    gap: 2,
                    '& > *': {
                        flex: 1,
                        minWidth: 0,
                    },
                    alignItems: 'stretch',
                }}
            >
                {hasDesks ? (
                    <Box sx={statCardSx}>
                        <Typography
                            variant='body2'
                            sx={statLabelSx}
                            id='dedicated-desks'
                        >
                            Dedicated desks
                        </Typography>
                        <Typography
                            variant='body2'
                            sx={statValueSx}
                            aria-labelledby='dedicated-desks'
                        >
                            {formatCount(reservation?.bookingInfo?.desks)}
                        </Typography>
                    </Box>
                ) : null}

                {hasPrivateOffices ? (
                    <Box sx={statCardSx}>
                        <Typography
                            variant='body2'
                            sx={statLabelSx}
                            id='private-offices'
                        >
                            {asset?.details?.subType === 'house'
                                ? 'Rooms'
                                : 'Private offices'}
                        </Typography>
                        <Typography
                            variant='body2'
                            sx={statValueSx}
                            aria-labelledby='private-offices'
                        >
                            {formatCount(
                                reservation?.bookingInfo?.privateOffices,
                            )}
                        </Typography>
                    </Box>
                ) : null}

                {hasMeetingRooms ? (
                    <Box sx={statCardSx}>
                        <Typography
                            variant='body2'
                            sx={statLabelSx}
                            id='meeting-rooms'
                        >
                            Meeting rooms
                        </Typography>
                        <Typography
                            variant='body2'
                            sx={statValueSx}
                            aria-labelledby='meeting-rooms'
                        >
                            {formatCount(
                                reservation?.bookingInfo?.meetingRooms,
                            )}
                        </Typography>
                    </Box>
                ) : null}

                {hasEntireVenue ? (
                    <Box sx={statCardSx}>
                        <Typography
                            variant='body2'
                            sx={statLabelSx}
                            id='entire-venue'
                        >
                            Reservation type
                        </Typography>
                        <Typography
                            variant='body2'
                            sx={{ ...statValueSx, fontSize: 16, mt: 0.75 }}
                            aria-labelledby='entire-venue'
                        >
                            Entire venue
                        </Typography>
                    </Box>
                ) : null}
            </Box>
        );
    };

    const renderNirovisionAccessCTA = () => {
        const enabled =
            asset?.usesNirovision || asset?.details?.dropoffUsesNirovision;
        if (!enabled) return null;

        const activeStaffCount =
            reservation?.staffAttending?.filter((u: Attendee) => !!u?.isActive)
                ?.length || 0;

        const guestCount = reservation?.externalAttendees?.length || 0;

        const selectedCount = activeStaffCount + guestCount;
        const needsAction = selectedCount === 0;

        const statusText = needsAction
            ? 'Action needed'
            : `${selectedCount} selected`;

        return (
            <Box
                sx={{
                    mt: 2,
                    borderRadius: 2,
                    // border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                    backgroundColor: theme.palette.neutral.main,
                    p: 1.5,
                    display: 'flex',
                    alignItems: isMobile ? 'stretch' : 'center',
                    gap: 1.5,
                    flexDirection: isMobile ? 'column' : 'row',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 3,
                        backgroundColor: needsAction
                            ? theme.palette.warning.main
                            : theme.palette.info.main,
                    }}
                />

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        flex: 1,
                        pl: 0.5,
                    }}
                >
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 999,
                            display: 'grid',
                            placeItems: 'center',
                            backgroundColor:
                                theme.palette.custom.cardActionBackground,
                            border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                        }}
                    >
                        <CameraEnhanceIcon fontSize='small' />
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            sx={{
                                fontWeight: 800,
                                fontSize: 14,
                                lineHeight: 1.2,
                            }}
                        >
                            Face entry access
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: 14,
                                color: needsAction
                                    ? theme.palette.warning.main
                                    : theme.palette.text.secondary,
                                lineHeight: 1.2,
                                fontWeight: 500,
                            }}
                        >
                            {statusText}
                        </Typography>
                    </Box>
                </Box>

                <Button
                    variant='contained'
                    color='primary'
                    size='small'
                    onClick={onOpenNirovisionModal}
                    startIcon={<CameraEnhanceIcon />}
                    sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        alignSelf: isMobile ? 'stretch' : 'center',
                    }}
                    fullWidth={isMobile}
                >
                    Manage access
                </Button>
            </Box>
        );
    };

    const renderRsvp = () => {
        const enabled = reservation?.rsvpConfig?.rsvpEnabled;

        const activeStaffCount =
            reservation?.staffAttending?.filter((u: Attendee) => !!u?.isActive)
                ?.length || 0;

        const guestCount = reservation?.externalAttendees?.length || 0;

        const selectedCount = activeStaffCount + guestCount;
        const needsAction = selectedCount === 0;

        const statusText = !enabled
            ? 'Not enabled'
            : needsAction
              ? 'Action needed'
              : `${selectedCount} selected`;

        return (
            <Box
                sx={{
                    mt: 2,
                    borderRadius: 2,
                    backgroundColor: theme.palette.neutral.main,
                    p: 1.5,
                    display: 'flex',
                    alignItems: isMobile ? 'stretch' : 'center',
                    gap: 1.5,
                    flexDirection: isMobile ? 'column' : 'row',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 3,
                        backgroundColor: needsAction
                            ? theme.palette.warning.main
                            : theme.palette.info.main,
                    }}
                />

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        flex: 1,
                        pl: 0.5,
                    }}
                >
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 999,
                            display: 'grid',
                            placeItems: 'center',
                            backgroundColor:
                                theme.palette.custom.cardActionBackground,
                            border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                        }}
                    >
                        <People fontSize='small' />
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            sx={{
                                fontWeight: 800,
                                fontSize: 14,
                                lineHeight: 1.2,
                            }}
                        >
                            RSVP
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: 14,
                                color: needsAction
                                    ? theme.palette.warning.main
                                    : theme.palette.text.secondary,
                                lineHeight: 1.2,
                                fontWeight: 500,
                            }}
                        >
                            {statusText}
                        </Typography>
                    </Box>
                </Box>

                <Button
                    variant='contained'
                    color='primary'
                    size='small'
                    onClick={onOpenRsvpModal}
                    startIcon={<People />}
                    sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        alignSelf: isMobile ? 'stretch' : 'center',
                    }}
                    fullWidth={isMobile}
                >
                    Manage RSVP
                </Button>
            </Box>
        );
    };

    const renderNirovisionOrGuestNoteAlert = () => {
        if (isOwnerBooker) return null;
        const baseSx = {
            mt: 2,
            boxShadow: 'none',
            borderRadius: 2,
            fontSize: 14,
            whiteSpace: 'normal' as const,
            wordBreak: 'break-word' as const,
            overflowWrap: 'break-word' as const,
            py: 2,
            justifyContent: 'left',
            textAlign: 'left',
            '& .MuiAlert-message': { width: '100%', textAlign: 'left' },
        };

        if (asset?.usesNirovision || asset?.details?.dropoffUsesNirovision) {
            return (
                <Alert severity='info' sx={baseSx}>
                    This experience uses facial recognition for entry. Select
                    all attendees so their profile pictures can be used for
                    access.
                </Alert>
            );
        }

        if (isOwnerBooker) {
            return (
                <Alert severity='info' sx={baseSx}>
                    This reservation was made by the host. Booker details and
                    company information are listed in the reservation details.
                </Alert>
            );
        }

        return (
            <Alert severity='info' sx={baseSx}>
                If attendees are not selected, they won’t receive this
                reservation in their 7DAY account.
            </Alert>
        );
    };

    const renderAttendees = (attendees: BookingAttendee[]) => {
        const accepted = attendees.filter(
            ({ rsvp: { status } }: BookingAttendee) => status === 'accepted',
        );
        const pending = attendees.filter(
            ({ rsvp: { status } }: BookingAttendee) => status === 'pending',
        );
        const declined = attendees.filter(
            ({ rsvp: { status } }: BookingAttendee) => status === 'declined',
        );
        const groups = [
            { attendees: accepted, header: `Accepted: ${accepted.length}` },
            { attendees: pending, header: `Didn't respond: ${pending.length}` },
            { attendees: declined, header: `Declined: ${declined.length}` },
        ];
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                }}
            >
                {groups.map(
                    ({ attendees, header }) =>
                        attendees.length > 0 && (
                            <>
                                {reservation?.rsvpConfig?.rsvpEnabled && (
                                    <Typography
                                        variant='body3'
                                        sx={{ ...statLabelSx, mt: 1 }}
                                    >
                                        {header}
                                    </Typography>
                                )}
                                {attendees.map(
                                    (user: BookingAttendee, index: number) => (
                                        <Typography
                                            key={String(
                                                user?.id ??
                                                    user?.email ??
                                                    index,
                                            )}
                                        >
                                            •{' '}
                                            <Box
                                                component='span'
                                                sx={{
                                                    textDecoration:
                                                        user?.isActive
                                                            ? 'none'
                                                            : 'line-through',
                                                }}
                                            >
                                                {getAttendeeLabel(user as any)}
                                            </Box>
                                            {!user?.isActive ? (
                                                <Box
                                                    component='span'
                                                    sx={{ ml: 0.5 }}
                                                >
                                                    (disabled)
                                                </Box>
                                            ) : null}
                                        </Typography>
                                    ),
                                )}
                            </>
                        ),
                )}
            </Box>
        );
    };

    const renderStaffColumn = () => {
        if (!staffVisible) return null;

        return (
            <Box sx={attendeeBoxSx}>
                <Typography
                    variant='body2'
                    sx={{
                        fontWeight: 900,
                        mb: 1,
                    }}
                >
                    Staff attending
                </Typography>

                {reservation?.staffAttending?.length > 0 ? (
                    renderAttendees(reservation?.staffAttending)
                ) : (
                    <Typography
                        variant='body2'
                        sx={{
                            color: theme.palette.text.secondary,
                        }}
                    >
                        No staff selected
                    </Typography>
                )}
            </Box>
        );
    };

    const renderGuestsColumn = () => {
        if (!guestVisible) return null;

        return (
            <Box sx={attendeeBoxSx}>
                <Typography variant='body2' sx={{ fontWeight: 900, mb: 1 }}>
                    Guests attending
                </Typography>

                {reservation?.externalAttendees?.length > 0 ? (
                    renderAttendees(reservation?.externalAttendees)
                ) : (
                    <Typography
                        variant='body2'
                        sx={{ color: theme.palette.text.secondary }}
                    >
                        No attendees selected
                    </Typography>
                )}
            </Box>
        );
    };

    return (
        <Accordion
            expanded={expanded}
            onChange={onToggle}
            disableGutters
            square={false}
            sx={outerAccordionSx}
        >
            <AccordionSummary
                aria-controls='reservation-guests-content'
                id='reservation-guests-header'
                sx={{
                    py: 1.25,
                    px: 0,
                    backgroundColor: theme.palette.neutral.main,
                }}
            >
                <Typography
                    variant='h2'
                    sx={{ letterSpacing: 0.2, color: 'text.secondary' }}
                >
                    Guests
                </Typography>
            </AccordionSummary>

            <AccordionDetails
                sx={{
                    pb: isMobile ? 2 : 3,
                    pt: 0,
                    px: 0,
                    backgroundColor: theme.palette.neutral.main,
                }}
            >
                <Divider sx={{ mt: 0, mb: 2 }} />

                {!isBlockedByOwner && (
                    <Box
                        sx={{
                            mb: 2,
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 2,
                            '& > *': {
                                flex: 1,
                                minWidth: 0,
                            },
                            alignItems: 'stretch',
                        }}
                    >
                        <Box sx={statCardSx}>
                            <Typography
                                variant='body2'
                                sx={statLabelSx}
                                id='adults'
                            >
                                {adultsLabel}
                            </Typography>
                            <Typography
                                variant='body2'
                                sx={statValueSx}
                                aria-labelledby='adults'
                            >
                                {adults}
                            </Typography>
                        </Box>

                        <Box sx={statCardSx}>
                            <Typography
                                variant='body2'
                                sx={statLabelSx}
                                id='children'
                            >
                                Children
                            </Typography>
                            <Typography
                                variant='body2'
                                sx={statValueSx}
                                aria-labelledby='children'
                            >
                                {children}
                            </Typography>
                        </Box>

                        <Box sx={statCardSx}>
                            <Typography
                                variant='body2'
                                sx={statLabelSx}
                                id='overnight-guests'
                            >
                                Overnight guests
                            </Typography>
                            <Typography
                                variant='body2'
                                sx={statValueSx}
                                aria-labelledby='overnight-guests'
                            >
                                {overnight}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {renderCommercialExtras()}

                {staffVisible || guestVisible ? (
                    <Box sx={cardSx}>
                        <Typography variant='h3' sx={sectionTitleSx}>
                            Attendees
                        </Typography>

                        <Box
                            sx={{
                                mt: 1,
                                display: 'grid',
                                gridTemplateColumns: isMobile
                                    ? '1fr'
                                    : 'repeat(2, minmax(0, 1fr))',
                                gap: 2,
                            }}
                        >
                            {staffVisible ? renderStaffColumn() : null}
                            {guestVisible ? renderGuestsColumn() : null}
                        </Box>

                        {loggedInUser?.role.includes('guest') &&
                            (['guest_admin', 'guest_department_admin'].includes(
                                loggedInUser?.role,
                            ) ||
                                [
                                    reservation?.booker?.id,
                                    reservation?.createdBy?.id,
                                ].includes(loggedInUser?.sub)) &&
                            renderRsvp()}
                        {renderNirovisionAccessCTA()}
                        {renderNirovisionOrGuestNoteAlert()}
                    </Box>
                ) : (
                    <>
                        {renderNirovisionAccessCTA()}
                        {renderNirovisionOrGuestNoteAlert()}
                    </>
                )}
            </AccordionDetails>
        </Accordion>
    );
}
